<script>
import Layout from '@layouts/adminLayout.vue'
import DebugLayout from '@layouts/debugLayout.vue'
import LoaderIcon from '@components/loader-icon.vue'
import FormItemGroup from '@components/form-item-group.vue'
import FormItem from '@components/form-item.vue'
import FormItemPhone from '@components/form-item-phone.vue'
import FormItemAddMultiple from '@components/form-item-add-multiple.vue'
import FormItemBirthDay from '@components/form-item-birth-day.vue'
import PersonnelFormLicenseStates from '@components/personnel/personnel-form-license-states.vue'
import TabContainer from '@components/tab-container-prototype.vue'
import PictureUpload from '@components/picture-upload.vue'
import ModalUserPmForm from '@components/personnel/modal-user-pm-form.vue'
import UserRosterPill from '@components/personnel/user-roster-pill.vue'
import PrimaryOpsContactPill from '@components/primary-ops-contact-pill.vue'
import { mottoOptInMobileMixin } from '@src/mixins/mottoOptInMobile'
import SimpleDivider from '@components/simple-divider.vue'
import TutorialStartButton from '@components/tutorial-start-button.vue'
import { initTour } from '@src/config/tourConfig'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { getMyUser, getMyUserDebug } from '@src/api/auth'
import { transform as transformUser } from '@src/api/transformers/userTransformer'
import { transformMany as transformOffices } from '@src/api/transformers/officeTransformer'
import _ from 'lodash'
import {
  getUserDropdownOptions,
  getMyOffices,
  getLoanProductionDataForOffice,
  getMyOfficesDebug,
  getLoanProductionPeriods,
} from '@src/api/personnelManagement'
import { arrayToSentence, userStatusFormatter } from '@utils/string-helpers'
import { formatDate } from '@utils/date-helpers'
import { authComputed } from '@state/helpers'
import { officePersonnelSteps } from '@utils/tutorials/officePersonnelSteps'
import router from '@router'
import ModalDeactivationPersonnel from '@components/personnel/modal-deactivation-personnel.vue'
import ModalOfficePmForm from '@components/personnel/modal-office-pm-form.vue'
import YtdLoGraph from '@components/personnel/ytd-lo-graph.vue'
import ResizeObserver from 'resize-observer-polyfill'
import TabContentContainer from '@components/tab-content-container.vue'

export default {
  page: {
    title: 'My Info',
    meta: [
      {
        name: 'description',
        content: 'The My Info page.',
      },
    ],
  },
  components: {
    TabContentContainer,
    ModalOfficePmForm,
    ModalDeactivationPersonnel,
    Layout,
    DebugLayout,
    LoaderIcon,
    TutorialStartButton,
    FormItemGroup,
    FormItem,
    FormItemPhone,
    FormItemBirthDay,
    FormItemAddMultiple,
    PersonnelFormLicenseStates,
    TabContainer,
    PictureUpload,
    ModalUserPmForm,
    UserRosterPill,
    PrimaryOpsContactPill,
    YtdLoGraph,
    SimpleDivider,
  },
  mixins: [mottoOptInMobileMixin, isMobileMixin],
  data() {
    return {
      loading: true,
      error: false,
      submitting: false,
      tour: null,
      hasTour: true,
      defaultPicture: require('@assets/images/logo-icon.svg'),
      hasOptedInEmail: [],
      shouldPromptHome: [],
      emailNotificationReferral: [],
      tabSelected: 'my-info',
      openedSections: [],
      openTabUsers: [],
      lastTab: {},
      hoverTimeoutId: null,
      shouldShowHoverTooltipTab: false,
      isRightmostTabOpen: false,
      selectedOfficeId: null,
      selectedUserId: null,
      currentModalUserPmForm: 'overview',
      currentModalOfficePmForm: 'website',
      user: {},
      offices: [],
      loanProductionData: [],
      leaderboardPeriods: [],
      debugMode: this.$attrs['debug-mode'] === 'yes',
      debugUserId: this.$attrs['debug-user-id'],
    }
  },
  computed: {
    ...authComputed,
    canUserManagePersonnel() {
      if (this.user && this.debugMode) {
        const userGroups = this.user?.groups?.map((x) => x.label) || []
        return userGroups.includes('Broker Owner') || userGroups.includes('Operations Manager')
      }

      return this.canManagePersonnel
    },
    pageTitle() {
      if (this.canUserManagePersonnel) {
        return `Profile / Office Management`
      }

      return `Profile`
    },
    picturePath() {
      if (this.errorLoading) {
        return this.defaultPicture
      }

      return this.getUser?.photoUrl || this.defaultPicture
    },
    orderedOffices() {
      return _.orderBy(this.offices, ['isPrimary', 'tradeName'], ['desc', 'asc'])
    },
    primaryOfficeId() {
      return this.currentUser.primaryOffice?.id
    },
    getTimeZoneOptions() {
      return _.values(this.timezoneOptions)
    },
    getTrainingData() {
      const trainings = this.user?.trainings || []
      return trainings.map((training) => (training.date ? `${training.name} - ${training.date}` : training.name))
    },
    getTabs() {
      let tabs = [
        {
          tabName: 'my-info',
          label: 'My Info',
        },
      ]

      if (!this.canUserManagePersonnel) {
        return tabs
      }

      for (const office of this.orderedOffices) {
        const usersToAddToTab = _.filter(this.openTabUsers, { openedByOfficeId: office.id })
        tabs = [
          ...tabs,
          {
            tabName: 'office',
            label: this.getOfficeTabLabel({
              office,
              usersInOfficeTab: usersToAddToTab.length,
            }),
            id: office.id,
          },
        ]

        if (this.tabSelected === 'my-info') {
          continue
        }

        if (this.tabSelected === 'user' && this.getSelectedUserOpenedByOfficeId !== office.id) {
          continue
        }

        if (this.tabSelected === 'office' && this.selectedOfficeId !== office.id) {
          continue
        }

        for (const user of usersToAddToTab) {
          tabs = [
            ...tabs,
            {
              tabName: 'user',
              label: `${user.firstName} ${user.lastName}`,
              id: user.id,
            },
          ]
        }
      }

      return tabs
    },

    getSelectedUserOpenedByOfficeId() {
      if (this.tabSelected !== 'user') {
        return
      }

      return _.find(this.openTabUsers, { id: this.selectedUserId }).openedByOfficeId
    },

    getUser() {
      if (this.selectedUserId) {
        return _.find(_.find(this.offices, { users: [{ id: this.selectedUserId }] }).users, { id: this.selectedUserId })
      }

      return this.user
    },

    getUserPrimaryOfficeLicenses() {
      const primaryOffice = _.find(this.offices, { id: this.getUser.primaryOffice?.id })
      const primaryOfficeLicenses = primaryOffice?.licenses || []
      return _.orderBy(primaryOfficeLicenses.map((license) => license.state))
    },

    getUserLicensesWithPending() {
      let licenses = this.getUser.licenses || []
      if (this.getUser.loReviewPending) {
        licenses = licenses.map((x) => ({
          state: x.number ? x.state : `${x.state} (pending)`,
          number: x.number ? `${x.number} (pending)` : '',
        }))
      }
      const pendingLicenses = this.getUser.pendingLicenses || []
      const pendingLicensesFormatted = pendingLicenses.map((x) => ({
        state: x.number ? x.state : `${x.state} (pending)`,
        number: x.number ? `${x.number} (pending)` : '',
      }))

      return [...licenses, ...pendingLicensesFormatted]
    },

    getOfficeLicensesWithPending() {
      const licenses = this.getOffice.licenses || []
      const pendingLicenses = this.getOffice.pendingLicenses || []
      const pendingLicensesFormatted = pendingLicenses.map((x) => ({
        state: x.number ? x.state : `${x.state} (pending)`,
        number: x.number ? `${x.number} (pending)` : '',
      }))

      return [...licenses, ...pendingLicensesFormatted]
    },

    getOffice() {
      return _.find(this.offices, { id: this.selectedOfficeId })
    },

    getOfficeGraphUsers() {
      return this.getOffice.users
    },

    getSelectedWebAdmin() {
      return _.find(this.getOffice.users, { id: this.getOffice?.webAdmin?.id }) || {}
    },

    canOfficeRequestLicense() {
      return !this.getOffice.isReadOnly && this.getOffice.canAddMoreLicenses && !this.getOffice.isLicenseChangePending
    },
  },

  created() {
    if (!this.hasMyInfoProfileFeature && !this.hasPersonnelFeature) {
      router.push({ name: 'home' })
    }

    this.loadMyUser({ debugUser: this.debugMode })
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(this.checkIfIsLastTabOpened)
    this.resizeObserver.observe(this.$refs.myInfoPage)
  },
  beforeDestroy() {
    this.resizeObserver.disconnect()
  },

  methods: {
    arrayToSentence,
    userStatusFormatter,
    formatDate,
    async tourCommands(command) {
      if (command === 'open-office-tab') {
        this.openTab('office', this.primaryOfficeId)
        await this.$nextTick()
      }

      if (command === 'open-office-website-section') {
        if (!this.openedSections.includes(`${this.getOffice.id}website`)) {
          this.openedSections = [...this.openedSections, `${this.getOffice.id}website`]
        }
        await this.$nextTick()
      }
    },
    startOfficePersonnelTutorial() {
      this.tour = initTour({ onCancelCallback: this.triggerAccessTutorialToast })
      const startTutorialModal = initTour({ confirmCancel: false })
      startTutorialModal.addStep({
        text: 'Would you like to begin the Office Management tutorial?',
        buttons: [
          {
            text: 'No',
            action: startTutorialModal.cancel,
          },
          {
            text: 'Yes',
            action: () => {
              startTutorialModal.complete()
              this.tour.start()
            },
          },
        ],
      })

      for (const step of officePersonnelSteps(this.tour, this.tourCommands)) {
        this.tour.addStep(step)
      }

      startTutorialModal.start()
    },

    triggerAccessTutorialToast() {
      const h = this.$createElement
      const vNodesMsg = h(
        'h4',
        {
          class: ['color-white', 'mb-0'],
          style: { lineHeight: '25px' },
        },
        [
          `You can access the tutorial at any time by clicking the`,
          h('MottoIcon', {
            props: {
              name: 'GraduationCap',
              'icon-color': 'orange',
            },
            class: ['mx-2'],
          }),
          `icon.`,
        ]
      )

      this.$root.$bvToast.toast([vNodesMsg], {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-primary-blue',
        autoHideDelay: 5000,
      })
    },

    openUserPmModal(form) {
      this.currentModalUserPmForm = form
      this.$bvModal.show('modalPmForm')
    },
    openOfficePmModal(form) {
      this.currentModalOfficePmForm = form
      this.$bvModal.show('officeModalPmForm')
    },
    openNewWebsitePaperform() {
      const url = 'https://newmottowebsite.paperform.co/'
      window.open(url, '_blank')
    },
    updateRoleRosterPill({ id }) {
      this.selectedUserId = id
      this.$nextTick(() => {
        this.openUserPmModal('roles')
      })
    },
    deactivateUserRosterPill({ id }) {
      this.selectedUserId = id
      this.$nextTick(() => {
        this.openDeactivateUserModal()
      })
    },
    viewProfileUserRosterPill(user, openNewTab = false) {
      if (!_.find(this.openTabUsers, { id: user.id })) {
        this.openTabUsers = [
          ...this.openTabUsers,
          {
            ...user,
            openedByOfficeId: this.selectedOfficeId,
          },
        ]
      }
      this.openTab('user', user.id, openNewTab)
    },

    openDeactivateUserModal() {
      this.$bvModal.show('modalDeactivateUser')
    },

    async loadMyUser({ debugUser = false }) {
      try {
        this.loading = true
        const requests = debugUser
          ? [getMyUserDebug(this.debugUserId), getMyOfficesDebug(this.debugUserId), getUserDropdownOptions()]
          : [getMyUser(), getMyOffices(), getUserDropdownOptions()]
        const [
          { data: user },
          {
            data: { data: offices },
          },
          {
            data: {
              data: { timezone: timezoneData },
            },
          },
          ,
        ] = await Promise.all(requests)
        this.getLoanProductionDataForOffices(offices.map((x) => x.id))
        this.timezoneOptions = timezoneData
        this.offices = transformOffices(offices, this.primaryOfficeId)
        this.offices = this.offices.filter((office) => !office.hideFromTabs)
        this.updateUserVariable(user)
        this.changeOffice(this.$route.params.selectOfficeId)
      } catch (err) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    updateEditedUser({ user, shouldRedirectToPhaseTwoRequest, isOnlyPhaseTwoChecked }) {
      if (shouldRedirectToPhaseTwoRequest) {
        this.$router.push({
          name: 'request-phase-two-access',
          params: {
            id: user.id,
            isOnlyPhaseTwoChecked,
          },
        })
        return
      }
      this.updateUserVariable(user)
    },
    updateEditedOffice({ office }) {
      this.offices = _.map(this.offices, (o) => (o.id === office.id ? office : o))
    },

    updateUserVariable(user) {
      user = transformUser(user)
      if (user.id === this.currentUser.id || (this.debugMode && user.id === this.debugUserId)) {
        this.user = user
        this.setAcceptedMobileOptInData(this.user.optInToMarketingTexts)
        this.hasOptedInEmail = this.user.optInToMarketingEmails ? ['1'] : []
        if (!this.hasPersonnelFeature) {
          this.user.groupLabels = this.user.groupLabels.map((x) => (x.includes('MLO') ? 'MLO' : x))
        }
        this.emailNotificationReferral = this.user.emailNotifications?.referral ? ['1'] : []
      }

      this.updateUserInAllOffices(user)
    },

    updateUserInAllOffices(user) {
      for (const office of this.offices) {
        office.users = office.users.map((oldUser) => (oldUser.id === user.id ? user : oldUser))
      }
    },

    async getLoanProductionDataForOffices(officeIds) {
      const {
        data: { data: periods },
      } = await getLoanProductionPeriods()
      const yearPeriods = _(periods)
        .map((x) => x.split('/')[1])
        .uniq()
        .sort((a, b) => b - a)
        .value()
      this.leaderboardPeriods = yearPeriods
      const productionDataRequests = _.flatten(
        officeIds.map((id) => {
          return yearPeriods.map((period) => getLoanProductionDataForOffice({ id, period }).catch((e) => e))
        })
      )
      Promise.all(productionDataRequests).then((data) => {
        this.loanProductionData = data.reduce((prev, curr) => {
          if (curr instanceof Error) {
            return prev
          }

          const officeId = curr?.data?.data['Office - Total Volume']?.[0]?.office_id

          if (!officeId) {
            return prev
          }

          const currOffice = prev[officeId] ?? {}
          return {
            ...prev,
            [officeId]: { ...currOffice, [curr.data.data.period]: curr.data.data },
          }
        }, {})
      })
    },

    getRosterForOffice({ users }, { onlyActive = false, onlyInactive = false }) {
      let validUsers = users.filter((x) => x.status !== 'created')
      if (!this.hasPersonnelFeature) {
        validUsers = validUsers.map((user) => ({
          ...user,
          groupLabels: user.groupLabels.map((x) => (x.includes('MLO') ? 'MLO' : x)),
        }))
      }
      const activeUsers = validUsers.filter((x) => !x.isInactive)
      const inactiveUsers = validUsers.filter((x) => x.isInactive)

      if (onlyActive) {
        return _.orderBy(activeUsers, ['createdAtRaw', 'lastName'], ['desc', 'asc'])
      }

      if (onlyInactive) {
        return _.orderBy(inactiveUsers, ['deactivateDate', 'lastName'], ['desc', 'asc'])
      }

      return [
        ..._.orderBy(activeUsers, ['createdAtRaw', 'lastName'], ['desc', 'asc']),
        ..._.orderBy(inactiveUsers, ['deactivateDate', 'lastName'], ['desc', 'asc']),
      ]
    },

    getRosterItemClass(user) {
      if (
        user.status === 'inactive' ||
        user.status === 'accessible_termination' ||
        user.groupLabels.includes('Record Only')
      ) {
        return [this.$style.rosterItemRed]
      }

      if (user.status === 'email_sent') {
        return [this.$style.rosterItemOrange]
      }

      return [this.$style.rosterItemGreen]
    },

    openTicket() {
      const ticketUrl = 'https://motto.happyfox.com/new/'
      window.open(ticketUrl, '_blank')
    },

    updatePhotoUrl(user) {
      this.updateUserVariable(user)
    },

    getTabClass({ tabName, id }) {
      if (tabName === 'user') {
        return `motto-tab-nav-link-with-x-padding motto-tab-background-selected`
      }

      if (tabName === 'my-info') {
        return 'flex-shrink-0'
      }

      if (tabName === 'office' && id === this.getSelectedUserOpenedByOfficeId) {
        return `motto-tab-background-selected`
      }

      return ''
    },

    getOfficeTabLabel({ office = {}, usersInOfficeTab = 0 }) {
      const duplicatedTradenames = new Set(
        _(this.orderedOffices.map((x) => x.tradeName))
          .groupBy()
          .pickBy((x) => x.length > 1)
          .keys()
          .value()
      )

      const hasSameTradename = duplicatedTradenames.has(office.tradeName)
      let label = hasSameTradename ? `${office.tradeName} ${office.city}, ${office.state}` : office.tradeName

      if (office.isReadOnly) {
        label = `${label} (Closed)`
      }

      if (
        usersInOfficeTab &&
        office.id !== this.getSelectedUserOpenedByOfficeId &&
        office.id !== this.selectedOfficeId
      ) {
        label = `${label} (${usersInOfficeTab})`
      }

      return label
    },

    isTabActive(tabName, id) {
      if (this.tabSelected !== tabName) {
        return false
      }

      if (tabName === 'my-info') {
        return true
      }

      if (tabName === 'office' && id === this.selectedOfficeId) {
        return true
      }

      return tabName === 'user' && id === this.selectedUserId
    },

    openTab(tab, id, openNewTab) {
      if (openNewTab) {
        return
      }

      this.selectedOfficeId = null
      this.selectedUserId = null
      this.tabSelected = tab

      if (tab === 'office') {
        this.selectedOfficeId = id
        this.$store.commit('personnel/SET_SELECTED_PERSONNEL_OFFICE', _.clone(this.getOffice))
      } else {
        this.$store.commit('personnel/SET_SELECTED_PERSONNEL_OFFICE', {})
      }

      if (tab === 'user') {
        this.selectedUserId = id
      }

      this.scrollToTabPosition(tab, id)
      this.checkIfIsLastTabOpened()
    },

    changeOffice(officeId) {
      if (officeId) {
        this.openTab('office', officeId)
      }
    },

    checkIfIsLastTabOpened() {
      const tab = this.tabSelected
      const id = tab === 'user' ? this.selectedUserId : this.selectedOfficeId
      this.$nextTick(() => {
        try {
          const tabRef = this.$refs[tab + id][0]
          const tabRefParent = tabRef?.parentElement?.parentElement
          if (!tabRef || !tabRefParent) {
            return
          }
          this.isRightmostTabOpen =
            parseInt(tabRef.getBoundingClientRect().right) === parseInt(tabRefParent.getBoundingClientRect().right)
        } catch (err) {
          this.isRightmostTabOpen = false
        }
      })
    },
    closeTab(tab, id) {
      const tabToClose = _.find(this.openTabUsers, { id: id })
      this.openTabUsers = _.filter(this.openTabUsers, (user) => user.id !== id)
      this.openTab('office', tabToClose.openedByOfficeId)
    },

    async scrollToTabPosition() {
      await this.$nextTick()

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    },

    setTabHoverTimeout() {
      this.hoverTimeoutId = setTimeout(() => {
        this.shouldShowHoverTooltipTab = true
      }, 1000)
    },

    clearTabHoverTimeout() {
      clearTimeout(this.hoverTimeoutId)
      this.hoverTimeoutId = null
      this.shouldShowHoverTooltipTab = false
    },
  },
}
</script>

<template>
  <component :is="debugMode ? 'DebugLayout' : 'Layout'">
    <div ref="myInfoPage">
      <LoaderIcon v-if="loading || error" :error="error" class="m-auto"></LoaderIcon>
      <div v-else>
        <BaseTitle>{{ pageTitle }}</BaseTitle>
        <TabContainer ref="tabContainer" :class="$style.tabContainer">
          <b-nav
            data-tour-tabs
            class="mw-100 width-fit-content motto-tabs align-items-end motto-tabs-mobile-100 mt-4"
            tabs
            @mouseenter="setTabHoverTimeout"
            @mouseleave="clearTabHoverTimeout">
            <b-nav-item
              v-for="tab in getTabs"
              :id="tab.tabName + tab.id"
              :key="tab.tabName + tab.id"
              :ref="tab.tabName + tab.id"
              data-my-info-tab
              :class="getTabClass(tab)"
              href="javascript:;"
              :active="isTabActive(tab.tabName, tab.id)"
              @click="openTab(tab.tabName, tab.id)">
              <span class="motto-tabs-label">
                <BaseIcon
                  class="mr-1"
                  :name="tab.tabName === 'office' ? 'building' : tab.tabName === 'user' ? 'building-user' : 'user'" />
                {{ tab.label }}
              </span>
              <span
                v-if="tab.tabName === 'user'"
                class="motto-tabs-close-X"
                @click.stop="closeTab(tab.tabName, tab.id)">
                <MottoIcon name="X" icon-color="gray" />
              </span>
              <b-tooltip
                :target="tab.tabName + tab.id"
                :placement="`top`"
                no-fade
                triggers="hover"
                :custom-class="
                  shouldShowHoverTooltipTab ? `pe-none tooltipGray light-transparent d-none d-lg-block` : `d-none`
                ">
                <p class="color-darkest-gray overline-text font-weight-bold">{{ tab.label }}</p>
              </b-tooltip>
            </b-nav-item>
          </b-nav>
          <template v-slot:content>
            <div v-if="tabSelected === 'my-info' || tabSelected === 'user'">
              <TabContentContainer
                :class="[$style.firstTabMargin, isRightmostTabOpen ? $style.lastContentTabOpen : '']">
                <div :class="$style.overviewContainer">
                  <div :class="$style.profilePictureContainer">
                    <div style="position: relative; width: min-content; margin: 0 auto">
                      <PictureUpload
                        :editable="!selectedUserId"
                        :user-id="getUser.id"
                        :class="$style.profilePictureUpload"
                        @update-photo="updatePhotoUrl" />
                      <div :class="$style.profilePictureImgWrapper">
                        <img
                          alt="My Profile Picture"
                          :src="picturePath"
                          :class="{ [$style.profilePictureImg]: true }"
                          @error="errorLoading = true" />
                      </div>
                    </div>
                    <h2 class="color-secondary-green text-center mt-3 mb-0">{{ getUser.name }}</h2>
                    <h3 v-if="getUser.jobTitle" class="color-secondary-green font-weight-normal text-center mt-1">
                      {{ getUser.jobTitle }}
                    </h3>
                    <p
                      v-for="label in _.take(getUser.groupLabels, 3)"
                      :key="label"
                      class="button-text color-primary-blue text-center">
                      {{ label }}
                    </p>
                    <template v-if="getUser.groupLabels.length === 4">
                      <p class="button-text color-primary-blue text-center">{{ getUser.groupLabels[3] }}</p>
                    </template>
                    <template v-else-if="getUser.groupLabels.length > 4">
                      <p
                        id="show-more-roles-tooltip"
                        class="width-fit-content mx-auto button-text color-primary-blue text-center">
                        + {{ getUser.groupLabels.length - 3 }} more
                      </p>
                      <b-tooltip
                        :target="`show-more-roles-tooltip`"
                        :placement="`bottom`"
                        custom-class="tooltipBlue"
                        no-fade>
                        <p
                          v-for="label in _.slice(getUser.groupLabels, 3)"
                          :key="label"
                          class="color-white text-center">
                          {{ label }}
                        </p>
                      </b-tooltip>
                    </template>
                  </div>
                  <div class="d-flex flex-grow-1 flex-min-width">
                    <div :class="$style.divider"></div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-content-end">
                        <div
                          v-if="!selectedUserId"
                          :class="$style.editIconContainerAbsolute"
                          @click.stop="openUserPmModal('overview')">
                          <MottoIcon :class="$style.editIcon" name="PenToSquare" icon-color="primary" />
                        </div>
                      </div>
                      <FormItemGroup>
                        <FormItemPhone
                          phone-label="Work Line"
                          :phone.sync="getUser.phone"
                          :phone-ext.sync="getUser.phoneExt"
                          rules="required|validPhoneNumber" />
                        <FormItemPhone
                          phone-label="Mobile Number"
                          :phone.sync="getUser.phoneMobile"
                          :phone-ext.sync="getUser.phoneMobileExt"
                          phone-ext-label="Ext. Mobile"
                          rules="validPhoneNumber" />
                      </FormItemGroup>
                      <FormItemGroup>
                        <FormItem label="Motto Email" :value="getUser.primaryEmail" />
                      </FormItemGroup>
                      <FormItemGroup>
                        <FormItem
                          v-if="!getUser.secondaryEmailTemp"
                          label="Personal Email"
                          :value.sync="getUser.secondaryEmail"
                          rules="required" />
                        <FormItem
                          v-if="getUser.secondaryEmailTemp"
                          label="Personal Email"
                          :value="getUser.secondaryEmailTemp"
                          :tooltip-text="'A confirmation has been sent to your personal email. Please verify your email address.'" />
                      </FormItemGroup>
                      <FormItemGroup>
                        <FormItem
                          label="Time Zone"
                          :rules="'required'"
                          :edit-select-values="getTimeZoneOptions"
                          :value="timezoneOptions[getUser.timeZone]" />
                        <FormItemBirthDay :birth-day.sync="getUser.birthDay" :birth-month.sync="getUser.birthMonth" />
                      </FormItemGroup>
                      <FormItemGroup>
                        <FormItem
                          label="Start Date"
                          :value="formatDate(getUser.createdAt, 'YYYY-MM-DD', 'MM/DD/yyyy')" />
                        <FormItem
                          v-if="getUser.isInactive && getUser.deactivateDate"
                          label="Termination Date"
                          :value="formatDate(getUser.deactivateDate, 'YYYY-MM-DD', 'MM/DD/yyyy')" />
                      </FormItemGroup>
                    </div>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer
                v-if="getUser.groupLabels.some((x) => x.includes('MLO'))"
                v-slot="{ flexColumnsPerRow }"
                :flex-columns-width="230">
                <div>
                  <div :class="$style.titleContainerWithButton">
                    <h1 :class="$style.tabTitle">Licensing</h1>
                    <BaseButton
                      v-if="
                        !selectedUserId &&
                        getUser.groupLabels.some((x) => x.includes('MLO Phase 2')) &&
                        !getUser.isLicenseChangePending
                      "
                      :options="{ defaultPadding: true }"
                      theme="white"
                      @click.stop="openUserPmModal('request_licenses')">
                      Request State License
                    </BaseButton>
                  </div>
                  <div class="mt-4">
                    <FormItemGroup>
                      <PersonnelFormLicenseStates label="State(s) of Licensure" :value="getUserLicensesWithPending" />
                      <div v-show="flexColumnsPerRow > 2"></div>
                      <FormItem label="NMLS" :value="getUser.nmlsNumber" />
                    </FormItemGroup>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer
                :class="!openedSections.includes(`${getUser.id}social-media`) ? $style.closedSection : ''"
                @click.native="
                  () => {
                    openedSections = [...openedSections, `${getUser.id}social-media`]
                  }
                ">
                <div>
                  <div class="d-flex justify-content-between align-items-center">
                    <h1 :class="$style.tabTitle">
                      Social Media
                      <BaseIcon
                        v-if="!openedSections.includes(`${getUser.id}social-media`)"
                        :class="$style.closeSectionArrow"
                        name="chevron-down" />
                      <BaseIcon
                        v-else
                        :class="$style.closeSectionArrow"
                        name="chevron-up"
                        @click.stop.native="
                          () => {
                            openedSections = openedSections.filter((x) => x !== `${getUser.id}social-media`)
                          }
                        " />
                    </h1>
                    <div
                      v-if="!selectedUserId"
                      :class="$style.editIconContainer"
                      @click.stop="openUserPmModal('marketing')">
                      <MottoIcon :class="$style.editIcon" name="PenToSquare" icon-color="primary" />
                    </div>
                  </div>
                  <div v-if="openedSections.includes(`${getUser.id}social-media`)" class="mt-4">
                    <FormItemGroup>
                      <FormItem label="Facebook" placeholder="facebook.com" :value.sync="getUser.facebook" />
                      <FormItem label="Twitter" placeholder="twitter.com" :value.sync="getUser.twitter" />
                      <FormItem label="LinkedIn" placeholder="linkedin.com" :value.sync="getUser.linkedin" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem label="Instagram" placeholder="instagram.com" :value.sync="getUser.instagram" />
                      <FormItem label="Snapchat" placeholder="snapchat.com" :value.sync="getUser.snapchat" />
                      <div></div>
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem label="Website" copyable placeholder="Website" :value="getUser.domain" />
                      <FormItem
                        label="Application URL"
                        copyable
                        placeholder="Application URL"
                        :value.sync="getUser.mortgageApplicLink" />
                      <FormItem label="Google My Business" placeholder="google.com" :value="getUser.google" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItemAddMultiple
                        :input-container-class="$style.keywordsInput"
                        :value.sync="getUser.keywords"
                        label="SEO Keywords"
                        placeholder="Add Keyword"></FormItemAddMultiple>
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem
                        label="ORM Location Description"
                        placeholder="ORM Location Description"
                        tooltip-text="The following will be used to fill in any website listing descriptions on websites such as Google, Bing, Yelp, and many more. If you would like something else please enter your own description below.  Limit is 750 characters."
                        :value.sync="getUser.locationDetails"
                        rules="max:750" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem
                        label="Short Web Description"
                        placeholder="Short Web Description"
                        tooltip-text="Add a short description of your business that will appear across the web where a short business description is available with your populated information.  Limit is 100 characters."
                        :value.sync="getUser.shortDescription"
                        rules="max:100" />
                    </FormItemGroup>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer
                :class="!openedSections.includes(`${getUser.id}notifications`) ? $style.closedSection : ''"
                @click.native="
                  () => {
                    openedSections = [...openedSections, `${getUser.id}notifications`]
                  }
                ">
                <div>
                  <div class="d-flex justify-content-between align-items-center">
                    <h1 :class="$style.tabTitle">
                      Notifications
                      <BaseIcon
                        v-if="!openedSections.includes(`${getUser.id}notifications`)"
                        :class="$style.closeSectionArrow"
                        name="chevron-down" />
                      <BaseIcon
                        v-else
                        :class="$style.closeSectionArrow"
                        name="chevron-up"
                        @click.stop.native="
                          () => {
                            openedSections = openedSections.filter((x) => x !== `${getUser.id}notifications`)
                          }
                        " />
                    </h1>
                    <div
                      v-if="!selectedUserId"
                      :class="$style.editIconContainer"
                      @click.stop="openUserPmModal('notifications')">
                      <MottoIcon :class="$style.editIcon" name="PenToSquare" icon-color="primary" />
                    </div>
                  </div>
                  <div v-if="openedSections.includes(`${getUser.id}notifications`)" class="mt-4">
                    <div v-if="hasSmsPromptsFeature" class="mb-4">
                      <div>
                        <h3>SMS</h3>
                        <div class="ml-3">
                          <h4 class="font-weight-bold">MottoMortgage Network Alerts</h4>
                          <p class="p1">
                            Opt in to receive important messages regarding marketing, events, and general information
                            about tools, services, and support of the Motto Mortgage network. Message frequency varies.
                            Text HELP to 58476 or call 866-668-8660 for help, STOP to 58476 to end. Message and Data
                            rates may apply. By opting in, you authorize Motto Mortgage to deliver marketing messages
                            using an automatic telephone dialing system. You are not required to opt in as a condition
                            of purchasing any property, goods, or services. By checking the box below, you agree to the
                            <RouterLink
                              :to="{ name: 'mobileTerms' }"
                              class="button-text text-decoration-none color-primary-blue"
                              target="_blank"
                              >MOBILE TERMS OF USE</RouterLink
                            >
                            and
                            <a
                              href="https://www.mottomortgage.com/privacy-policy"
                              target="_blank"
                              class="button-text text-decoration-none color-primary-blue"
                              >Motto Mortgage Privacy Policy</a
                            >
                            and
                            <RouterLink
                              :to="{ name: 'userAgreement' }"
                              target="_blank"
                              class="button-text text-decoration-none color-primary-blue"
                              >USER AGREEMENT</RouterLink
                            >.
                          </p>
                          <div :class="$style.optInMobileNumberContainer" class="d-flex mt-3 align-items-center">
                            <BaseCheckbox
                              v-model="acceptedMobileOptInData"
                              disabled
                              label="I’d like to receive text messages from Motto HQ."
                              input-value="1" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mb-4">
                      <h3>Email</h3>
                      <div class="ml-3">
                        <h4 class="font-weight-bold">Network Connect</h4>
                        <div>
                          <p class="p1">
                            Opt-in to receive email notifications for Network Connect deal-level referrals.
                          </p>
                        </div>
                        <div :class="$style.optInMobileNumberContainer" class="d-flex mt-3 align-items-center">
                          <BaseCheckbox
                            v-model="emailNotificationReferral"
                            disabled
                            label="Deal-level referral email notifications"
                            input-value="1" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabContentContainer>
            </div>
            <div v-if="tabSelected === 'office'">
              <TabContentContainer
                :class="[$style.firstTabMargin, isRightmostTabOpen ? $style.lastContentTabOpen : '']">
                <div :class="$style.officeOverviewContainer">
                  <div :class="$style.officeOverviewDataContainer" data-tour-office-data>
                    <div class="flex-grow-1">
                      <h1 class="break-word color-primary-blue">
                        Motto Mortgage
                        <br />
                        {{ getOffice.tradeName }}
                      </h1>
                      <div class="mt-4 mb-4">
                        <h3 class="color-primary-blue">{{ getOffice.addressStreet }}</h3>
                        <h3 class="color-primary-blue">{{ getOffice.getStateAddress() }}</h3>
                      </div>
                      <FormItemGroup :item-min-width="160">
                        <FormItem
                          label="Office Open Date"
                          :value="
                            getOffice.actualOpenDate
                              ? formatDate(getOffice.actualOpenDate, 'YYYY-MM-DD', 'MM/DD/yyyy')
                              : ''
                          " />
                        <FormItem
                          label="Office Main Contact"
                          :value="getOffice.mainContactName"
                          tooltip-text="The Main Office Contact is not published on the web, and only serves as the main point of contact between your Office and Motto HQ." />
                      </FormItemGroup>
                      <FormItemGroup :item-min-width="160">
                        <FormItemPhone
                          phone-label="Office Phone"
                          :phone.sync="getOffice.phone"
                          :phone-ext.sync="getOffice.phoneExt" />
                        <FormItem
                          v-if="
                            getOffice.terminationDate &&
                            getOffice.status !== 'Open' &&
                            getOffice.status !== 'To Be Opened'
                          "
                          label="Office Closed Date"
                          :value="formatDate(getOffice.terminationDate, 'YYYY-MM-DD', 'MM/DD/yyyy')" />
                      </FormItemGroup>
                    </div>
                    <template v-if="getOffice.primaryOpsContact">
                      <p class="color-dark-gray mt-10 mb-10">Motto HQ contact:</p>
                      <PrimaryOpsContactPill :primary-ops-contact="getOffice.primaryOpsContact"></PrimaryOpsContactPill>
                    </template>
                  </div>
                  <div :class="$style.officeOverviewGraphContainer">
                    <div :class="$style.graphDivider"></div>
                    <div :class="$style.officeGraphsContainer" data-tour-office-graph>
                      <YtdLoGraph
                        :office="getOffice"
                        :users="getOfficeGraphUsers"
                        :loan-production-data="loanProductionData[getOffice.id]"
                        :periods="leaderboardPeriods"
                        @click-user-picture="(user) => viewProfileUserRosterPill(user, true)" />
                    </div>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer
                :class="!openedSections.includes(`${getOffice.id}licensing`) ? $style.closedSection : ''"
                @click.native="
                  () => {
                    openedSections = [...openedSections, `${getOffice.id}licensing`]
                  }
                ">
                <div>
                  <div :class="$style.titleContainerWithButton">
                    <h1 :class="$style.tabTitle">
                      Licensing
                      <BaseIcon
                        v-if="!openedSections.includes(`${getOffice.id}licensing`)"
                        :class="$style.closeSectionArrow"
                        name="chevron-down" />
                      <BaseIcon
                        v-else
                        :class="$style.closeSectionArrow"
                        name="chevron-up"
                        @click.stop.native="
                          () => {
                            openedSections = openedSections.filter((x) => x !== `${getOffice.id}licensing`)
                          }
                        " />
                    </h1>
                    <div data-closed-section-button>
                      <BaseButton
                        v-if="canOfficeRequestLicense"
                        data-tour-office-request-license
                        :options="{ defaultPadding: true }"
                        theme="white"
                        @click.stop="openOfficePmModal('request_licenses')">
                        Request State License
                      </BaseButton>
                    </div>
                  </div>
                  <div v-if="openedSections.includes(`${getOffice.id}licensing`)" class="mt-4">
                    <FormItemGroup>
                      <FormItem label="License Status" :value="getOffice.licensingStatus" />
                      <FormItem label="NMLS" :value="getOffice.entityNMLS" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <PersonnelFormLicenseStates label="State(s) of Licensure" :value="getOfficeLicensesWithPending" />
                    </FormItemGroup>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer
                :class="!openedSections.includes(`${getOffice.id}website`) ? $style.closedSection : ''"
                @click.native="
                  () => {
                    openedSections = [...openedSections, `${getOffice.id}website`]
                  }
                ">
                <div data-tour-office-website>
                  <div
                    :class="!getOffice.isIntegratedWithLHP ? $style.titleContainerWithButton : ''"
                    class="d-flex justify-content-between align-items-center">
                    <h1 :class="$style.tabTitle">
                      Website
                      <BaseIcon
                        v-if="!openedSections.includes(`${getOffice.id}website`)"
                        :class="$style.closeSectionArrow"
                        name="chevron-down" />
                      <BaseIcon
                        v-else
                        :class="$style.closeSectionArrow"
                        name="chevron-up"
                        @click.stop.native="
                          () => {
                            openedSections = openedSections.filter((x) => x !== `${getOffice.id}website`)
                          }
                        " />
                    </h1>
                    <div
                      v-if="!getOffice.isReadOnly && getOffice.isIntegratedWithLHP"
                      :class="$style.editIconContainer"
                      @click="openOfficePmModal('website')">
                      <MottoIcon :class="$style.editIcon" name="PenToSquare" icon-color="primary" />
                    </div>
                    <BaseButton
                      v-else-if="!getOffice.isReadOnly"
                      :options="{ defaultPadding: true }"
                      theme="white"
                      @click.stop="openNewWebsitePaperform">
                      Activate Website
                    </BaseButton>
                  </div>
                  <div v-if="openedSections.includes(`${getOffice.id}website`)" class="mt-4">
                    <FormItemGroup>
                      <FormItem label="Website" copyable :value="getOffice.website" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem label="Vanity Url" copyable :value="getOffice.vanityUrl" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem
                        label="Web Admin"
                        :value="getSelectedWebAdmin.name"
                        tooltip-text="The Website Admin designation grants this user access to the main office website admin settings.  Only one user may be designated as the Website Admin at this time." />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem
                        label="ORM Business Description"
                        :value.sync="getOffice.locationDetails"
                        tooltip-text="This description appears on the main office website in the About Us section, and on listings such as Google My Business. "
                        :rules="'required|max:750'" />
                    </FormItemGroup>
                    <FormItemGroup>
                      <FormItem
                        label="Short Web Description"
                        :value.sync="getOffice.shortDescription"
                        rules="max:100"
                        tooltip-text="Add a short description of your business that will appear across the web where a short business description is available with your populated information.  Limit is 100 characters." />
                    </FormItemGroup>
                  </div>
                </div>
              </TabContentContainer>
              <TabContentContainer>
                <div>
                  <div :class="$style.titleContainerWithButton">
                    <h1 :class="$style.tabTitle">Roster</h1>
                    <RouterLink
                      v-if="!getOffice.isReadOnly"
                      data-tour-add-new-user
                      class="router-link-anchor"
                      :to="{ name: 'personnel-management-user-creation' }">
                      <BaseButton theme="white">Add New User</BaseButton>
                    </RouterLink>
                  </div>
                </div>
                <div class="mt-4" :class="$style.officeRosterUserContainer">
                  <UserRosterPill
                    v-for="officeUser in getRosterForOffice(getOffice, { onlyActive: true })"
                    :key="officeUser.id"
                    data-tour-user-roster-pill
                    :user="officeUser"
                    :read-only="getOffice.isReadOnly"
                    @update-role="updateRoleRosterPill"
                    @deactivate-user="deactivateUserRosterPill"
                    @view-profile="viewProfileUserRosterPill"></UserRosterPill>
                </div>
                <SimpleDivider :margin-x="0" :margin-y="50"></SimpleDivider>
                <div :class="$style.officeRosterUserContainer">
                  <UserRosterPill
                    v-for="officeUser in getRosterForOffice(getOffice, { onlyInactive: true })"
                    :key="officeUser.id"
                    :user="officeUser"
                    :read-only="getOffice.isReadOnly"
                    @update-role="updateRoleRosterPill"
                    @deactivate-user="deactivateUserRosterPill"
                    @view-profile="viewProfileUserRosterPill"></UserRosterPill>
                </div>
              </TabContentContainer>
            </div>
          </template>
        </TabContainer>
        <div data-tour-help-button :class="$style.helpButtonContainer">
          <h4 class="text-center color-primary-charcoal font-weight-bold">Need help updating your information?</h4>
          <BaseButton theme="lightBlue" @click="openTicket">
            <span>Submit a ticket</span>
          </BaseButton>
        </div>
      </div>
      <ModalUserPmForm
        :user-to-edit="getUser"
        :primary-office-licenses="getUserPrimaryOfficeLicenses"
        :form="currentModalUserPmForm"
        :custom-class="currentModalUserPmForm === 'request_licenses' ? 'form-modal-small' : ''"
        @update-user="updateEditedUser" />
      <ModalOfficePmForm
        :office-to-edit="getOffice"
        :form="currentModalOfficePmForm"
        :custom-class="currentModalOfficePmForm === 'request_licenses' ? 'form-modal-small' : ''"
        @update-office="updateEditedOffice" />
      <ModalDeactivationPersonnel :user="getUser" @update-user="updateUserVariable" />
      <TutorialStartButton
        v-if="hasTour && canUserManagePersonnel && !isMobile"
        :start-tutorial-fn="startOfficePersonnelTutorial" />
    </div>
  </component>
</template>

<style lang="scss" module>
@import '@design';

.profilePictureImgWrapper {
  @media (max-width: $lg-breakpoint) {
    width: 150px;
    height: 150px;
    margin-top: 10px;
  }

  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 50%;
}

.titleWithTooltip {
  @media (max-width: $lg-breakpoint) {
    width: unset;
  }

  position: relative;
  width: min-content;
  white-space: nowrap;

  div[data-tooltip-icon] {
    top: 2px;
    right: -30px;
  }
}

.profilePictureImg {
  width: 100%;
  height: 100%;
  transform: scale(1.05);
  object-fit: cover;
}

.mobileTooltip {
  @media (max-width: $lg-breakpoint) {
    display: block;
    margin: -8px 0 8px;
    color: $secondary-light-blue;
    white-space: normal;
  }

  display: none;
}

.profilePictureUpload {
  position: absolute;
  top: -10px;
  right: -20px;
  z-index: 20;
}

.buttonsContainer {
  @media (max-width: $lg-breakpoint) {
    justify-content: center;
    margin-top: 20px;
  }

  display: flex;
  justify-content: right;
  margin-right: 20px;
}

.optInMobileNumberContainer {
  @media (max-width: $lg-breakpoint) {
    flex-wrap: wrap;
  }
}

.optInMobileNumber {
  @media (max-width: $lg-breakpoint) {
    width: 100%;
    margin-top: 20px;
    margin-left: unset;
  }

  margin-left: 30px;

  p {
    display: none;
  }
}

.eventsTextBreak {
  ul {
    word-break: break-word;
  }
}

.officeRosterUserContainer {
  @media (max-width: $lg-breakpoint) {
    grid-template-columns: auto;
  }

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  grid-gap: 50px 50px;
}

.keywordsInput {
  max-width: 350px;
}

.helpButtonContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  margin: 80px auto 0;

  button {
    width: 193px;
    margin-top: 18px;
  }
}

.tabContainer {
  span[data-required-asterisk] {
    display: none;
  }
}

.tabTopMarginWithoutButtons {
  @media (max-width: $lg-breakpoint) {
    margin-top: 0;
  }

  margin-top: 45px;
}

.lastContentTabOpen {
  border-top-right-radius: 0 !important;
}

.tabTitle {
  @media (max-width: $sm-breakpoint) {
    font-size: 24px;
  }

  margin-bottom: 0;
  color: $primary-blue;
}

.firstTabMargin {
  padding: 30px 25px;
  margin-top: 0;
}

.overviewContainer {
  @media (max-width: $sm-breakpoint) {
    flex-direction: column;
  }

  display: flex;
}

.officeOverviewContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;
  flex-wrap: wrap;
}

.officeOverviewDataContainer {
  @media (max-width: $lg-breakpoint) {
    width: 100%;
    min-width: unset;
  }

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: calc(50% - 75px);
  min-width: calc(50% - 75px);
}

.officeOverviewGraphContainer {
  @media (max-width: $lg-breakpoint) {
    width: 100%;
    min-width: unset;
    margin-top: 50px;
  }

  display: flex;
  flex-grow: 1;
  width: calc(50% + 75px);
  min-width: calc(50% + 75px);
}

.profilePictureContainer {
  @media (max-width: $sm-breakpoint) {
    margin-bottom: 20px;
  }

  min-width: min(50%, 400px);
  margin: auto;

  p {
    margin-top: 8px;
  }
}

.divider {
  @media (max-width: $sm-breakpoint) {
    display: none;
  }

  flex-shrink: 0;
  width: 1px;
  height: 100%;
  margin: 0 35px;
  background: $light-gray;
  background: linear-gradient(0deg, $white 0%, $light-gray 50%, $white 100%);
  border: 0;
}

.graphDivider {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  flex-shrink: 0;
  width: 1px;
  height: 100%;
  margin: 0 35px;
  background: $light-gray;
  background: linear-gradient(0deg, $white 0%, $light-gray 50%, $white 100%);
  border: 0;
}

.editIconContainerAbsolute {
  @media (max-width: $lg-breakpoint) {
    transform: scale(0.9);
  }

  position: absolute;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 49px;
  height: 49px;
  cursor: pointer;
  border-radius: 50%;

  svg {
    width: 25px;
    height: 25px;
  }

  &:hover {
    background: $lightest-gray;
  }

  .editIcon {
    margin-top: -4px;
    cursor: pointer;
  }
}

.editIconContainer {
  @media (max-width: $lg-breakpoint) {
    transform: scale(0.9);
  }

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 49px;
  height: 49px;
  cursor: pointer;
  border-radius: 50%;

  svg {
    width: 25px;
    height: 25px;
  }

  &:hover {
    background: $lightest-gray;
  }

  .editIcon {
    margin-top: -4px;
    cursor: pointer;
  }
}

.officeGraphsContainer {
  @media (max-width: $sm-breakpoint) {
    margin: 0 0 20px;
  }

  @media (max-width: $lg-breakpoint) {
    width: 100%;
    min-width: unset;
    max-width: unset;
    margin-bottom: 20px;
  }

  flex-grow: 1;
  max-width: calc(100% - 75px);
  height: 100%;

  p {
    margin-top: 8px;
  }
}

.titleContainerWithButton {
  @media (max-width: $lg-breakpoint) {
    flex-wrap: wrap;
    gap: 25px 0;
    h1 {
      margin-right: 10px;
    }

    button {
      margin-bottom: 10px;
    }
  }

  display: flex;
  justify-content: space-between;
}

.closedSection {
  cursor: pointer;

  &:hover:not(:has(button:hover), :has([data-closed-section-button]:hover)) {
    background: $lightest-gray;
  }
}

.closeSectionArrow {
  margin-left: 18px;
  cursor: pointer;
}
</style>
